import { useEffect } from "react";
import { useLocation } from "react-router-dom";
import { publicRoutes } from "@/i18n/routes";

const DOMAIN = "https://dutchdryice.com";

/**
 * Adds hreflang <link> tags to <head> for the current page.
 * This helps Google understand the NL/EN relationship.
 */
export default function HreflangTags() {
  const { pathname } = useLocation();

  useEffect(() => {
    // Remove ALL hreflang tags (both pre-rendered ones from prerender-meta.mjs
    // and any previously-injected ones). We then re-add a single, correct set
    // for the current SPA route. This prevents duplicate hreflang entries
    // (e.g. "More than one page is linked for the same language" in Ahrefs)
    // when navigating client-side between routes.
    document
      .querySelectorAll('link[rel="alternate"][hreflang]')
      .forEach((el) => el.remove());

    // Determine current language and clean path
    const isEn = pathname.startsWith("/en/") || pathname === "/en";
    const cleanPath = isEn ? (pathname.slice(3) || "/") : pathname;

    // Find matching route
    const lang = isEn ? "en" : "nl";
    const route = publicRoutes.find(r => r[lang] === cleanPath);

    if (!route) return;

    const nlUrl = `${DOMAIN}${route.nl === "/" ? "" : route.nl}`;
    const enUrl = `${DOMAIN}/en${route.en === "/" ? "" : route.en}`;

    // Add hreflang links
    const addLink = (hreflang: string, href: string) => {
      const link = document.createElement("link");
      link.rel = "alternate";
      link.hreflang = hreflang;
      link.href = href;
      link.setAttribute("data-hreflang", "true");
      document.head.appendChild(link);
    };

    addLink("nl", nlUrl);
    addLink("en", enUrl);
    addLink("x-default", nlUrl);

    // NOTE: Do NOT set canonical here. PageMeta is the single source of truth
    // for the canonical URL (self-canonical per language). Setting it from two
    // places caused EN pages to receive an NL canonical, which Ahrefs flagged
    // as "Hreflang to non-canonical" and "Non-canonical page in sitemap".
  }, [pathname]);

  return null;
}
